import React from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import PauseCircleOutlineOutlinedIcon from '@mui/icons-material/PauseCircleOutlineOutlined';
import HourglassEmptyOutlinedIcon from '@mui/icons-material/HourglassEmptyOutlined';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined';

/**
 * SummaryCard - Card individual de KPI
 */
function SummaryCard({ title, value, icon, iconColor, iconBg }) {
  return (
    <Card
      sx={{
        borderRadius: '12px',
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
        border: '1px solid #E8ECF0',
        p: '20px',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        transition: 'box-shadow 0.2s ease',
        '&:hover': {
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontWeight: 500, mb: 0.5, fontSize: '0.8125rem' }}
          >
            {title}
          </Typography>
          <Typography
            variant="h4"
            sx={{ fontWeight: 700, fontSize: '1.75rem', color: 'text.primary', lineHeight: 1 }}
          >
            {value}
          </Typography>
        </Box>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '10px',
            bgcolor: iconBg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {React.cloneElement(icon, { sx: { color: iconColor, fontSize: 20 } })}
        </Box>
      </Box>
    </Card>
  );
}

/**
 * CompanySummaryCards - Cards de KPI no topo da listagem de empresas (Admin Geral)
 * @param {Object} props
 * @param {{ total: number, active: number, inactive: number, never_accessed: number, test: number, grace: number, expired: number }} props.summary
 */
function CompanySummaryCards({ summary }) {
  const isMobile = useMediaQuery('(max-width: 900px)');
  const s = summary || { total: 0, active: 0, inactive: 0, never_accessed: 0, test: 0, grace: 0, expired: 0 };

  const cards = [
    {
      title: 'Total de empresas',
      value: s.total,
      icon: <ApartmentOutlinedIcon />,
      iconColor: '#5A80FB',
      iconBg: 'rgba(90, 128, 251, 0.1)',
    },
    {
      title: 'Ativas',
      value: s.active,
      icon: <CheckCircleOutlineOutlinedIcon />,
      iconColor: '#2E7D32',
      iconBg: '#E8F5E9',
    },
    {
      title: 'Inativas',
      value: s.inactive,
      icon: <PauseCircleOutlineOutlinedIcon />,
      iconColor: '#E65100',
      iconBg: '#FFF3E0',
    },
    {
      title: 'Nunca acessaram',
      value: s.never_accessed,
      icon: <HourglassEmptyOutlinedIcon />,
      iconColor: '#616161',
      iconBg: '#F5F5F5',
    },
    {
      title: 'Teste',
      value: s.test,
      icon: <ScienceOutlinedIcon />,
      iconColor: '#4527A0',
      iconBg: '#EDE7F6',
    },
    {
      title: 'Em encerramento',
      value: s.grace ?? 0,
      icon: <EventNoteOutlinedIcon />,
      iconColor: '#E65100',
      iconBg: '#FFF3E0',
    },
    {
      title: 'Carência vencida',
      value: s.expired ?? 0,
      icon: <EventBusyOutlinedIcon />,
      iconColor: '#B71C1C',
      iconBg: '#FDECEA',
    },
  ];

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fit, minmax(150px, 1fr))',
        gap: 2,
        mb: 3,
      }}
    >
      {cards.map((card, index) => (
        <SummaryCard key={index} {...card} />
      ))}
    </Box>
  );
}

export default CompanySummaryCards;
